import { useEffect, useState } from 'react'
import {
  FaArrowLeft,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaMapMarkerAlt,
  FaRoute,
  FaShieldAlt,
  FaSignOutAlt,
  FaUser,
  FaUsers,
  FaSatelliteDish,
  FaPlus,
  FaTrash
} from 'react-icons/fa'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'
import api from 'services/api'

import * as S from '../style'
import * as R from './style'

type RadarPost = {
  id: string
  type: 'offer' | 'partner' | 'content'
  category?: string | null
  title: string
  slug: string
  subtitle?: string | null
  excerpt?: string | null
  content?: string | null
  coverImage?: string | null
  status: 'draft' | 'published'
  featured: boolean
  partnerName?: string | null
  partnerLogo?: string | null
  externalUrl?: string | null
  ctaLabel?: string | null
  price?: number | null
  oldPrice?: number | null
  currency?: string
  metaLine?: string | null
  expiresAt?: string | null
  seoTitle?: string | null
  seoDescription?: string | null
  readingMinutes?: number | null
}

type RadarHomePreview = {
  offers: Array<{ id: string }>
  partners: Array<{ id: string }>
  contents: Array<{ id: string }>
}

const normalizeExternalUrl = (value?: string | null) => {
  const url = String(value || '').trim()
  if (!url) return ''

  if (/^https?:\/\//i.test(url)) return url
  if (/^\/\//.test(url)) return `https:${url}`

  return `https://${url.replace(/^\/+/, '')}`
}

const emptyForm = {
  type: 'offer',
  category: '',
  title: '',
  slug: '',
  subtitle: '',
  excerpt: '',
  content: '',
  coverImage: '',
  status: 'draft',
  featured: false,
  partnerName: '',
  partnerLogo: '',
  externalUrl: '',
  ctaLabel: '',
  price: '',
  oldPrice: '',
  currency: 'BRL',
  metaLine: '',
  expiresAt: '',
  seoTitle: '',
  seoDescription: '',
  readingMinutes: ''
}

const AdminRadar = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [form, setForm] = useState<any>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<RadarPost | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')
  const isPublisherOnly = Boolean(user?.isPublisher && !user?.isAdmin)
  const roleLabel = user?.isMaster
    ? 'Master'
    : user?.isAdmin
      ? 'Admin'
      : 'Publisher'

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['admin-radar'],
    queryFn: async () => {
      const response = await api.get<RadarPost[]>('/admin/radar')
      return response.data
    }
  })

  const { data: publicRadar, isLoading: isPublicRadarLoading } = useQuery({
    queryKey: ['radar-home'],
    queryFn: async () => {
      const response = await api.get<RadarHomePreview>('/radar', {
        params: { _t: Date.now() },
        timeout: 10000
      })
      return response.data
    },
    staleTime: 0
  })

  const selected = posts.find((item) => item.id === selectedId)
  const visibleOfferIds = new Set(
    publicRadar?.offers?.map((item) => item.id) ?? []
  )
  const visiblePartnerIds = new Set(
    publicRadar?.partners?.map((item) => item.id) ?? []
  )
  const visibleContentIds = new Set(
    publicRadar?.contents?.map((item) => item.id) ?? []
  )

  const placementLabel = (item: RadarPost) => {
    if (item.status !== 'published') return 'Rascunho'
    if (!publicRadar) return 'Publicado'

    if (item.type === 'content') {
      return visibleContentIds.has(item.id)
        ? 'Publicado · Na vitrine'
        : 'Publicado · Arquivo'
    }

    if (item.type === 'offer') {
      return visibleOfferIds.has(item.id)
        ? 'Publicado · No carrossel'
        : 'Publicado · Fora do carrossel'
    }

    return visiblePartnerIds.has(item.id)
      ? 'Publicado · No carrossel'
      : 'Publicado · Fora do carrossel'
  }

  useEffect(() => {
    if (!selected) return
    setForm({
      ...emptyForm,
      ...selected,
      price: selected.price ?? '',
      oldPrice: selected.oldPrice ?? '',
      expiresAt: selected.expiresAt ? selected.expiresAt.slice(0, 16) : '',
      readingMinutes: selected.readingMinutes ?? ''
    })
  }, [selected])

  const set = (key: string, value: any) =>
    setForm((current: any) => ({ ...current, [key]: value }))

  const reset = () => {
    setSelectedId(null)
    setForm(emptyForm)
  }

  const uploadImage = async (file?: File) => {
    if (!file) return
    setUploading(true)
    try {
      const data = new FormData()
      data.append('file', file)
      const response = await api.post('/users/uploadFile', data)
      set('coverImage', response.data.locationInS3)
      toast.success('Imagem enviada.', { theme: 'dark' })
    } catch {
      toast.error('Não foi possível enviar a imagem.', { theme: 'dark' })
    } finally {
      setUploading(false)
    }
  }

  const save = async () => {
    if (!form.title?.trim()) {
      toast.error('Informe um título.', { theme: 'dark' })
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...form,
        price: form.price === '' ? null : Number(form.price),
        oldPrice: form.oldPrice === '' ? null : Number(form.oldPrice),
        readingMinutes:
          form.readingMinutes === '' ? null : Number(form.readingMinutes),
        expiresAt: form.expiresAt || null,
        externalUrl: normalizeExternalUrl(form.externalUrl) || null
      }

      if (selectedId) {
        await api.patch('/admin/radar/' + selectedId, payload)
      } else {
        await api.post('/admin/radar', payload)
      }

      await queryClient.invalidateQueries({ queryKey: ['admin-radar'] })
      await queryClient.invalidateQueries({ queryKey: ['radar-home'] })
      await queryClient.invalidateQueries({ queryKey: ['radar-news'] })
      toast.success('Publicação salva.', { theme: 'dark' })
      reset()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível salvar.',
        { theme: 'dark' }
      )
    } finally {
      setSaving(false)
    }
  }

  const confirmRemove = async () => {
    if (!deleteTarget || deleting) return

    setDeleting(true)
    try {
      await api.delete('/admin/radar/' + deleteTarget.id)
      await queryClient.invalidateQueries({ queryKey: ['admin-radar'] })
      await queryClient.invalidateQueries({ queryKey: ['radar-home'] })
      await queryClient.invalidateQueries({ queryKey: ['radar-news'] })
      if (selectedId === deleteTarget.id) reset()
      toast.success('Publicação excluída.', { theme: 'dark' })
      setDeleteTarget(null)
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível excluir.',
        { theme: 'dark' }
      )
    } finally {
      setDeleting(false)
    }
  }

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaShieldAlt /> {roleLabel}
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            {!isPublisherOnly && (
              <>
                <S.SidebarNavLink to="/admin">
                  <FaChartLine />
                  Dashboard
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/usuarios">
                  <FaUsers />
                  Usuários
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/localidades">
                  <FaMapMarkerAlt />
                  Localidades
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/roteiros">
                  <FaRoute />
                  Roteiros
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/anuncios">
                  <FaBullhorn />
                  Anúncios
                </S.SidebarNavLink>
              </>
            )}
            <S.SidebarNavLink to="/admin/radar" $active>
              <FaSatelliteDish />
              Radar
            </S.SidebarNavLink>
            {!isPublisherOnly && (
              <S.SidebarNavLink to="/admin/comissoes">
                <FaDollarSign />
                Comissões
              </S.SidebarNavLink>
            )}
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser /> Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft /> Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna Admin</S.TopbarEyebrow>
            <S.TopbarTitle>Radar</S.TopbarTitle>
          </div>
          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img src={user.profilePicture} alt="" />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || roleLabel}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.SignOutButton type="button" onClick={signOut}>
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <R.Content>
          <R.Header>
            <div>
              <h1>Conteúdo do Radar</h1>
              <p>Cadastre ofertas, parceiros, notícias, guias e dicas.</p>
            </div>
            <button type="button" onClick={reset}>
              <FaPlus /> Nova publicação
            </button>
          </R.Header>

          <R.Layout>
            <R.ListPanel>
              <h2>Publicações</h2>
              <R.ShowcaseSummary>
                <strong>Exibição pública</strong>
                <span>
                  {isPublicRadarLoading
                    ? 'Calculando vitrine...'
                    : `Notícias ${publicRadar?.contents?.length ?? 0}/4 · Ofertas ${publicRadar?.offers?.length ?? 0}/6 · Parceiros ${publicRadar?.partners?.length ?? 0}/6`}
                </span>
                <small>
                  Notícias excedentes ficam no arquivo. Ofertas e parceiros
                  excedentes permanecem publicados, mas fora do carrossel.
                </small>
              </R.ShowcaseSummary>
              {isLoading ? (
                <R.Empty>Carregando...</R.Empty>
              ) : posts.length ? (
                posts.map((item) => (
                  <R.PostRow
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    data-active={selectedId === item.id}
                  >
                    <div>
                      <span>{item.type}</span>
                      <strong>{item.title}</strong>
                      <small>
                        {placementLabel(item)}
                        {item.featured ? ' · Destaque' : ''}
                      </small>
                    </div>
                    <R.DeleteButton
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        setDeleteTarget(item)
                      }}
                    >
                      <FaTrash />
                    </R.DeleteButton>
                  </R.PostRow>
                ))
              ) : (
                <R.Empty>Nenhuma publicação ainda.</R.Empty>
              )}
            </R.ListPanel>

            <R.FormPanel>
              <R.FormGrid>
                <R.Field>
                  <label>Tipo</label>
                  <select
                    value={form.type}
                    onChange={(e) => set('type', e.target.value)}
                  >
                    <option value="offer">Oferta</option>
                    <option value="partner">Parceiro</option>
                    <option value="content">Conteúdo / notícia</option>
                  </select>
                </R.Field>
                <R.Field>
                  <label>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => set('status', e.target.value)}
                  >
                    <option value="draft">Rascunho</option>
                    <option value="published">Publicado</option>
                  </select>
                </R.Field>
                <R.Field $wide>
                  <label>Título</label>
                  <input
                    value={form.title}
                    onChange={(e) => set('title', e.target.value)}
                  />
                </R.Field>
                <R.Field>
                  <label>Categoria</label>
                  <input
                    value={form.category}
                    onChange={(e) => set('category', e.target.value)}
                    placeholder="Passagens, Guia, Notícia..."
                  />
                </R.Field>
                <R.Field>
                  <label>Slug opcional</label>
                  <input
                    value={form.slug}
                    onChange={(e) => set('slug', e.target.value)}
                  />
                </R.Field>
                <R.Field $wide>
                  <label>Resumo curto</label>
                  <textarea
                    rows={3}
                    value={form.excerpt}
                    onChange={(e) => set('excerpt', e.target.value)}
                  />
                </R.Field>
                <R.Field $wide>
                  <label>Conteúdo</label>
                  <textarea
                    rows={8}
                    value={form.content}
                    onChange={(e) => set('content', e.target.value)}
                  />
                </R.Field>
                <R.Field $wide>
                  <label>Imagem de capa</label>
                  <R.UploadRow>
                    <input
                      value={form.coverImage}
                      onChange={(e) => set('coverImage', e.target.value)}
                      placeholder="URL da imagem"
                    />
                    <label>
                      {uploading ? 'Enviando...' : 'Enviar arquivo'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => uploadImage(e.target.files?.[0])}
                      />
                    </label>
                  </R.UploadRow>
                </R.Field>

                {form.type !== 'content' && (
                  <>
                    <R.Field>
                      <label>Preço</label>
                      <input
                        type="number"
                        value={form.price}
                        onChange={(e) => set('price', e.target.value)}
                      />
                    </R.Field>
                    <R.Field>
                      <label>Preço anterior</label>
                      <input
                        type="number"
                        value={form.oldPrice}
                        onChange={(e) => set('oldPrice', e.target.value)}
                      />
                    </R.Field>
                    <R.Field>
                      <label>Parceiro / fornecedor</label>
                      <input
                        value={form.partnerName}
                        onChange={(e) => set('partnerName', e.target.value)}
                      />
                    </R.Field>
                    <R.Field>
                      <label>Validade</label>
                      <input
                        type="datetime-local"
                        value={form.expiresAt}
                        onChange={(e) => set('expiresAt', e.target.value)}
                      />
                    </R.Field>
                    <R.Field $wide>
                      <label>Linha de apoio</label>
                      <input
                        value={form.metaLine}
                        onChange={(e) => set('metaLine', e.target.value)}
                        placeholder="Ida e volta · fev–abr/2027"
                      />
                    </R.Field>
                    <R.Field $wide>
                      <label>Link externo / afiliado</label>
                      <input
                        value={form.externalUrl}
                        onChange={(e) => set('externalUrl', e.target.value)}
                        placeholder="https://parceiro.com ou www.parceiro.com"
                      />
                    </R.Field>
                    <R.Field>
                      <label>Texto do botão</label>
                      <input
                        value={form.ctaLabel}
                        onChange={(e) => set('ctaLabel', e.target.value)}
                      />
                    </R.Field>
                    <R.Field>
                      <label>Logo do parceiro (URL)</label>
                      <input
                        value={form.partnerLogo}
                        onChange={(e) => set('partnerLogo', e.target.value)}
                      />
                    </R.Field>
                  </>
                )}

                {form.type === 'content' && (
                  <R.Field>
                    <label>Minutos de leitura</label>
                    <input
                      type="number"
                      value={form.readingMinutes}
                      onChange={(e) => set('readingMinutes', e.target.value)}
                    />
                  </R.Field>
                )}

                <R.Field>
                  <label>SEO title</label>
                  <input
                    value={form.seoTitle}
                    onChange={(e) => set('seoTitle', e.target.value)}
                  />
                </R.Field>
                <R.Field>
                  <label>Destaque</label>
                  <R.Check>
                    <input
                      type="checkbox"
                      checked={Boolean(form.featured)}
                      onChange={(e) => set('featured', e.target.checked)}
                    />
                    {form.type === 'content'
                      ? 'Priorizar na vitrine'
                      : 'Priorizar no carrossel'}
                  </R.Check>
                  <R.FieldHelp>
                    {form.type === 'content'
                      ? 'A vitrine mostra 4 conteúdos. Os demais continuam em Ver todas as notícias.'
                      : 'Cada carrossel mostra até 6 publicações. Destaques ganham prioridade.'}
                  </R.FieldHelp>
                </R.Field>
                <R.Field $wide>
                  <label>SEO description</label>
                  <textarea
                    rows={2}
                    value={form.seoDescription}
                    onChange={(e) => set('seoDescription', e.target.value)}
                  />
                </R.Field>
              </R.FormGrid>

              <R.Actions>
                <button type="button" className="secondary" onClick={reset}>
                  Cancelar
                </button>
                <button type="button" onClick={save} disabled={saving}>
                  {saving
                    ? 'Salvando...'
                    : selectedId
                      ? 'Salvar alterações'
                      : 'Criar publicação'}
                </button>
              </R.Actions>
            </R.FormPanel>
          </R.Layout>
        </R.Content>
      </S.Main>

      {deleteTarget && (
        <R.ConfirmOverlay
          role="presentation"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <R.ConfirmDialog
            role="dialog"
            aria-modal="true"
            aria-labelledby="radar-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <R.ConfirmIcon>
              <FaTrash />
            </R.ConfirmIcon>
            <h2 id="radar-delete-title">Excluir publicação?</h2>
            <p>
              Você está prestes a excluir <strong>{deleteTarget.title}</strong>.
              Esta ação não pode ser desfeita.
            </p>
            <R.ConfirmActions>
              <button
                type="button"
                className="secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="danger"
                onClick={confirmRemove}
                disabled={deleting}
              >
                {deleting ? 'Excluindo...' : 'Excluir publicação'}
              </button>
            </R.ConfirmActions>
          </R.ConfirmDialog>
        </R.ConfirmOverlay>
      )}
    </S.Page>
  )
}

export default AdminRadar
